import { appSession } from '../lib/safeStorage';
import apiClient from './apiClient';

// ==================== Staff ====================

export const staffAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/staff', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/staff/${id}`);
    return response.data;
  },

  getMyProfile: async () => {
    const response = await apiClient.get('/staff/me');
    return response.data;
  },

  create: async (staffData) => {
    const response = await apiClient.post('/staff', staffData);
    return response.data;
  },

  update: async (id, staffData) => {
    const response = await apiClient.put(`/staff/${id}`, staffData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/staff/${id}`);
    return response.data;
  },

  uploadPhoto: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/upload/staff-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Update staff member's own profile (for staff members)
  updateMyProfile: async (staffData) => {
    const response = await apiClient.put('/staff/me', staffData);
    return response.data;
  },

  // Persist STAFFED PRO certification on the current user's own staff
  // record — called once after they complete every "Show me" walkthrough.
  markStaffedPro: async () => {
    const response = await apiClient.post('/staff/me/staffed-pro');
    return response.data;
  },

  getRoles: async () => {
    const response = await apiClient.get('/staff/roles/list');
    return response.data;
  },

  getRoleCounts: async () => {
    const response = await apiClient.get('/staff/roles/counts');
    return response.data;
  },

  // Role Management CRUD APIs
  getAllRoles: async () => {
    const response = await apiClient.get('/roles');
    return response.data;
  },

  createRole: async (roleData) => {
    const response = await apiClient.post('/roles', roleData);
    return response.data;
  },

  updateRole: async (roleId, roleData) => {
    const response = await apiClient.put(`/roles/${roleId}`, roleData);
    return response.data;
  },

  deleteRole: async (roleId) => {
    const response = await apiClient.delete(`/roles/${roleId}`);
    return response.data;
  },

  reorderRoles: async (roleOrders) => {
    const response = await apiClient.put('/roles/reorder', roleOrders);
    return response.data;
  },

  getEarnings: async (staffId) => {
    const response = await apiClient.get(`/staff/${staffId}/earnings`);
    return response.data;
  },

  // Heat Meter APIs
  awardHeatPoints: async (staffId, action, eventId = null) => {
    const response = await apiClient.post(`/staff/${staffId}/heat-points`, {
      staffId,
      action,
      eventId,
      points: 0 // Backend calculates actual points
    });
    return response.data;
  },

  getHeatHistory: async (staffId) => {
    const response = await apiClient.get(`/staff/${staffId}/heat-history`);
    return response.data;
  },

  // Staff availability for date range
  checkAvailability: async (params) => {
    const { fromDate, fromTime, toDate, toTime, role } = params;
    const response = await apiClient.get('/staff/availability', {
      params: {
        from_date: fromDate,
        from_time: fromTime || '00:00',
        to_date: toDate,
        to_time: toTime || '23:59',
        role: role || 'all',
      },
    });
    return response.data;
  },

  resetHeatMeter: async (staffId) => {
    const response = await apiClient.post(`/staff/${staffId}/reset-heat`);
    return response.data;
  },

  // Rating APIs
  rateStaff: async (staffId, ratingData) => {
    const response = await apiClient.post(`/staff/${staffId}/rate`, ratingData);
    return response.data;
  },

  getRatings: async (staffId) => {
    const response = await apiClient.get(`/staff/${staffId}/ratings`);
    return response.data;
  },

  // Leaderboard
  getHeatLeaderboard: async (period = 'month') => {
    const response = await apiClient.get('/leaderboard/heat', { params: { period } });
    return response.data;
  },
};

// ==================== Awards API ====================

export const awardsAPI = {
  getStaffOfTheYear: async () => {
    const response = await apiClient.get('/awards/staff-of-the-year');
    return response.data;
  },

  awardStaffOfTheYear: async (staffId) => {
    const response = await apiClient.post('/awards/staff-of-the-year/award', { staffId });
    return response.data;
  },

  getHistory: async () => {
    const response = await apiClient.get('/awards/history');
    return response.data;
  },
};

// ==================== Team API ====================

export const teamAPI = {
  getMembers: async () => {
    const response = await apiClient.get('/team/members');
    return response.data;
  },

  addMember: async (memberData) => {
    const response = await apiClient.post('/team/members', memberData);
    return response.data;
  },

  updateMember: async (memberId, memberData) => {
    const response = await apiClient.put(`/team/members/${memberId}`, memberData);
    return response.data;
  },

  deleteMember: async (memberId) => {
    const response = await apiClient.delete(`/team/members/${memberId}`);
    return response.data;
  },

  resendInvite: async (memberId) => {
    const response = await apiClient.post(`/team/members/${memberId}/resend-invite`);
    return response.data;
  },

  updateAccessLevel: async (memberId, accessLevel) => {
    const response = await apiClient.put(`/team/members/${memberId}/access-level`, { accessLevel });
    return response.data;
  },

  getManagerCount: async () => {
    const response = await apiClient.get('/team/managers/count');
    return response.data;
  },

  // Sync pending team member statuses (auto-fix pending members who have accounts)
  syncStatus: async () => {
    const response = await apiClient.post('/team/sync-status');
    return response.data;
  },

  // Add team member as staff (for event assignments)
  addAsStaff: async (memberId, staffData = {}) => {
    const response = await apiClient.post(`/team/members/${memberId}/add-as-staff`, staffData);
    return response.data;
  },
};

// Add to staffAPI
staffAPI.linkManagerToStaff = async () => {
  const response = await apiClient.post('/staff/link-manager-staff');
  return response.data;
};

staffAPI.getMyStaffProfile = async () => {
  const response = await apiClient.get('/staff/my-staff-profile');
  return response.data;
};

// Get event history for a staff member
staffAPI.getEventHistory = async (staffId) => {
  const response = await apiClient.get(`/staff/${staffId}/event-history`);
  return response.data;
};

// Upload attachment for a staff member
staffAPI.uploadAttachment = async (staffId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`/staff/${staffId}/upload-attachment`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Delete attachment from a staff member
staffAPI.deleteAttachment = async (staffId, attachmentId) => {
  const response = await apiClient.delete(`/staff/${staffId}/attachment/${attachmentId}`);
  return response.data;
};

// Send registration invite to a staff member
staffAPI.sendInvite = async (staffId) => {
  const response = await apiClient.post(`/staff-register/send-invite/${staffId}`);
  return response.data;
};

// Bulk invite staff members
staffAPI.bulkInvite = async (staffIds) => {
  const response = await apiClient.post('/staff/bulk-invite', { staffIds });
  return response.data;
};

// Bulk delete staff members
staffAPI.bulkDelete = async (staffIds) => {
  const response = await apiClient.post('/staff/bulk-delete', { staffIds });
  return response.data;
};

// Bulk inactivate staff members
staffAPI.bulkInactivate = async (staffIds) => {
  const response = await apiClient.post('/staff/bulk-inactivate', { staffIds });
  return response.data;
};

// Bulk activate staff members
staffAPI.bulkActivate = async (staffIds) => {
  const response = await apiClient.post('/staff/bulk-activate', { staffIds });
  return response.data;
};

// Bulk assign role to staff members
staffAPI.bulkAssignRole = async (staffIds, roleName) => {
  const response = await apiClient.post('/staff/bulk-assign-role', { staffIds, roleName });
  return response.data;
};

// Staff self-service assignment APIs
staffAPI.getMyAssignments = async () => {
  // When an admin is previewing a SPECIFIC crew member, read that person's
  // assignments through the admin door instead of the caller's own. Same
  // backend function, same code path - `/staff/admin/assignments-preview/{id}`
  // calls `_assignments_for_staff` exactly as `/staff/my-assignments` does, so
  // there is no second implementation to drift.
  // Read straight from appSession rather than threading context through
  // every caller. It fails CLOSED: a stale or foreign id 404s server-side
  // because the endpoint is scoped to the caller's own staff.
  let previewStaffId = null;
  try {
    if (appSession.getItem('rolePreview') === 'staff') {
      previewStaffId = appSession.getItem('rolePreviewStaffId') || null;
    }
  } catch { /* storage blocked (in-app webviews) - fall through to self */ }
  const path = previewStaffId
    ? `/staff/admin/assignments-preview/${previewStaffId}`
    : '/staff/my-assignments';
  const response = await apiClient.get(path);
  return response.data;
};

staffAPI.confirmAssignment = async (eventId) => {
  const response = await apiClient.post(`/staff/confirm-assignment/${eventId}`);
  return response.data;
};

staffAPI.declineAssignment = async (eventId) => {
  const response = await apiClient.post(`/staff/decline-assignment/${eventId}`);
  return response.data;
};

staffAPI.unassignSelf = async (eventId) => {
  const response = await apiClient.post(`/staff/unassign-self/${eventId}`);
  return response.data;
};

